import { EventStatus } from '@deltager/organizers/admin/modules/events/create-event/store/event-setup.state';
import { Event } from '@deltager/organizers/admin/modules/events/create-event/model/event.model';
import { Operation } from 'fast-json-patch';
import { EventHeader } from '../model/event-header';
import { EventSetupWizardStep } from '@deltager/organizers/admin/modules/events/create-event/model/event-setup-wizard-step.enum';
import { Action } from '@ngrx/store';
import { AccountingSystem } from '@deltager/organizers/admin/shared/models/AccountingSystem';
import { EventSetupError } from '@deltager/organizers/admin/modules/events/create-event/model/event-error.model';
import { SaveChangesSource } from '@deltager/organizers/admin/redux/root.state';

export enum EventActionTypes {
  ConstructEventRequest = '[Event setup] Construct request',
  ConstructEventSuccess = '[Event setup] Construct success',
  LoadEventRequest = '[Event setup] Load request',
  LoadEventSuccess = '[Event setup] Load success',
  CreateEventRequest = '[Event setup] Create request',
  CreateEventSuccess = '[Event setup] Create success',
  CreateEventAndAddSessionsRequest = '[Event setup] Create request and add sessions',
  UpdateEventRequest = '[Event setup] Update request',
  UpdateEventSuccess = '[Event setup] Update success',
  PublishEventRequest = '[Event setup] Publish request',
  PublishEventSuccess = '[Event setup] Publish success',
  GeneralEventUpdateError = '[Event setup] Caught error while modifying / creating event',
  ChangeEventStep = '[Event setup] Change step',
  Error = '[Event setup] ERROR',
  EditEvent = '[Event setup] Edit',
  ChangeEventStatus = '[Event setup] Change status',
  MarkEventAsFree = '[Event setup] Mark as free',
  MarkEventAsPaid = '[Event setup] Mark as paid',
  DisableVat = '[Event setup] Disable VAT',
  UpdateAccountingSystem = '[Event setup] Update accounting system',
  OpenRepeatingEventPopup = '[Event setup] Open repeating event popup',
  CloseRepeatingEventPopup = '[Event setup] Close repeating event popup',
  SetLastBankAccountId = '[Event setup] Set last bank account id',
  ClearEventStore = '[Event setup] Clear event store',
  LoadEventHeaderSuccess = '[Event setup] Load event header',
  SetMasterTotalSubEvents = '[Event setup] Set master total sub events count',
}

interface EventPayload {
  event: Event;
}

// Construct event
export class ConstructEventRequestAction implements Action {
  readonly type = EventActionTypes.ConstructEventRequest;

  constructor(public payload: { wizardId: number; eventType: number; preferCached: boolean }) {}
}

export class ConstructEventSuccessAction implements Action {
  readonly type = EventActionTypes.ConstructEventSuccess;

  constructor(public payload: EventPayload) {}
}

// Load event
export class LoadEventRequestAction implements Action {
  readonly type = EventActionTypes.LoadEventRequest;

  constructor(public payload: { eventId: number }) {}
}

export class LoadEventSuccessAction implements Action {
  readonly type = EventActionTypes.LoadEventSuccess;

  constructor(public payload: { event: Event; eventStats: EventHeader }) {}
}

// Create event
export class CreateEventRequestAction implements Action {
  readonly type = EventActionTypes.CreateEventRequest;

  constructor(
    public payload: { event: Event; notificationMessage: string; wizardId: number; source?: SaveChangesSource },
  ) {}
}

// Create event and sessions
export class CreateEventAndAddSessionsRequestAction implements Action {
  readonly type = EventActionTypes.CreateEventAndAddSessionsRequest;

  constructor(
    public payload: {
      event: Event;
      notificationMessage: string;
      wizardId?: number;
      sessionPayload: any;
      source?: SaveChangesSource;
    },
  ) {}
}

export class CreateEventSuccessAction implements Action {
  readonly type = EventActionTypes.CreateEventSuccess;

  constructor(public payload: EventPayload) {}
}

// Update event
export class UpdateEventRequestAction implements Action {
  readonly type = EventActionTypes.UpdateEventRequest;

  constructor(
    public payload: {
      event: Event;
      notificationMessage: string;
      source?: SaveChangesSource;
      eventOperations: Operation[];
    },
  ) {}
}

export class UpdateEventSuccessAction implements Action {
  readonly type = EventActionTypes.UpdateEventSuccess;

  constructor(public payload: EventPayload) {}
}

// Publish event
export class PublishEventRequestAction implements Action {
  readonly type = EventActionTypes.PublishEventRequest;

  constructor(public payload: { event: Event; notificationMessage: string; eventOperations: Operation[] }) {}
}

export class PublishEventSuccessAction implements Action {
  readonly type = EventActionTypes.PublishEventSuccess;

  constructor(public payload: EventPayload) {}
}

export class GeneralEventUpdateErrorAction implements Action {
  readonly type = EventActionTypes.GeneralEventUpdateError;
}

// Change event status
export class ChangeEventStatusAction implements Action {
  readonly type = EventActionTypes.ChangeEventStatus;

  constructor(public payload: { status: EventStatus }) {}
}

// Change event step
export class ChangeEventStepAction implements Action {
  readonly type = EventActionTypes.ChangeEventStep;

  constructor(public payload: { step: EventSetupWizardStep }) {}
}

// Error
export class EventErrorAction implements Action {
  readonly type = EventActionTypes.Error;

  constructor(public payload: EventSetupError) {}
}

// Edit event
export class EditEventAction implements Action {
  readonly type = EventActionTypes.EditEvent;

  constructor(public payload: EventPayload) {}
}

// Mark event as free
export class MarkEventAsFreeAction implements Action {
  readonly type = EventActionTypes.MarkEventAsFree;
}

// Mark event as paid
export class MarkEventAsPaidAction implements Action {
  readonly type = EventActionTypes.MarkEventAsPaid;
}

// Disable VAT
export class DisableVatAction implements Action {
  readonly type = EventActionTypes.DisableVat;
}

// Update accounting system
export class UpdateAccountingSystemAction implements Action {
  readonly type = EventActionTypes.UpdateAccountingSystem;

  constructor(public accountingSystem?: AccountingSystem) {}
}

// Open repeating event popup
export class OpenRepeatingEventPopupAction implements Action {
  readonly type = EventActionTypes.OpenRepeatingEventPopup;

  constructor(public eventId: number) {}
}

// Close repeating event popup
export class CloseRepeatingEventPopupAction implements Action {
  readonly type = EventActionTypes.CloseRepeatingEventPopup;
}

// Set last bank account id
export class SetLastBankAccountIdAction implements Action {
  readonly type = EventActionTypes.SetLastBankAccountId;

  constructor(public lastBankAccountId: number) {}
}

// Clear event store
export class ClearEventStoreAction implements Action {
  readonly type = EventActionTypes.ClearEventStore;
}

// Load event header
export class LoadEventHeaderSuccessAction implements Action {
  readonly type = EventActionTypes.LoadEventHeaderSuccess;

  constructor(public payload: { eventHeader: EventHeader }) {}
}

// Set master total sub events
export class SetMasterTotalSubEventsAction implements Action {
  readonly type = EventActionTypes.SetMasterTotalSubEvents;

  constructor(public totalSubEvents: number) {}
}
